import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Typography,
} from "@mui/material";
import {
  ModalActionButtonStyles,
  ModalBaseStyles,
  ModalHeader,
} from "../Common/styles/modal";
import { useAppDispatch, useAppSelector } from "../../Redux/hooks";
import { toggleResponseState } from "../../Redux/reducers/responseSlice";
import { errorToastMessage } from "../../utils/toast";
import { useParams } from "react-router-dom";
import http from "../../utils/http";
import IncompleteScreeningConfirmModal from "../Responses/Screening/IncompleteScreeningConfirmModal";
import { IncompleteScreeningForm } from "../../utils/screeningEditEligibility";
import { fetchIncompleteScreeningSummary } from "../../utils/screeningIncompleteSummary";

type Props = {
  showModal: boolean;
  closeModal: () => void;
  variant?: "registry" | "study";
  /** When true, parent already ran incomplete preview (study FormHeader path). */
  skipIncompleteCheck?: boolean;
};

const FinishScreeningModal = ({
  showModal,
  closeModal,
  variant = "registry",
  skipIncompleteCheck = false,
}: Props) => {
  const { id: studyId, participantId } = useParams();
  const [submitLoader, setSubmitLoader] = useState(false);
  const [checkingIncomplete, setCheckingIncomplete] = useState(false);
  const [incompleteCheckDone, setIncompleteCheckDone] = useState(false);
  const [showIncompleteModal, setShowIncompleteModal] = useState(false);
  const [incompleteForms, setIncompleteForms] = useState<
    IncompleteScreeningForm[]
  >([]);

  const dispatch = useAppDispatch();
  const screeningAttemptId = useAppSelector(
    (state) => state.response.screeningAttemptId
  );

  const title = useMemo(
    () => (variant === "study" ? "Save and Complete" : "Save Screening"),
    [variant]
  );

  useEffect(() => {
    if (!showModal) {
      setShowIncompleteModal(false);
      setIncompleteForms([]);
      setCheckingIncomplete(false);
      setIncompleteCheckDone(false);
      return;
    }

    if (skipIncompleteCheck) {
      setIncompleteCheckDone(true);
      return;
    }

    if (!participantId || !screeningAttemptId) {
      setIncompleteCheckDone(true);
      return;
    }

    let cancelled = false;
    setCheckingIncomplete(true);
    void fetchIncompleteScreeningSummary(
      participantId,
      screeningAttemptId,
      variant === "registry" ? { isRegistry: true } : { studyId }
    )
      .then((incomplete) => {
        if (cancelled) return;
        if (incomplete.length > 0) {
          setIncompleteForms(incomplete);
          setShowIncompleteModal(true);
        }
      })
      .catch(() => {
        // Proceed to save confirm if preview fails
      })
      .finally(() => {
        if (!cancelled) {
          setCheckingIncomplete(false);
          setIncompleteCheckDone(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    showModal,
    skipIncompleteCheck,
    participantId,
    screeningAttemptId,
    variant,
    studyId,
  ]);

  const finishUrl = () => {
    const params = new URLSearchParams({
      participantId: participantId || "",
    });
    if (variant === "study") {
      return `/study/${studyId}/admin-screening-responses/attempt/${screeningAttemptId}/finish?${params}`;
    }
    return `/registry/admin-screening-responses/attempt/${screeningAttemptId}/finish?${params}`;
  };

  const postFinish = async () => {
    try {
      setSubmitLoader(true);
      await http.post(finishUrl());
      closeModal();
      dispatch(toggleResponseState());
    } catch (err) {
      errorToastMessage(err as Error);
      setSubmitLoader(false);
    }
  };

  const handleClose = () => {
    if (submitLoader) return;
    setShowIncompleteModal(false);
    closeModal();
  };

  const showSaveConfirm =
    showModal &&
    !showIncompleteModal &&
    (skipIncompleteCheck || incompleteCheckDone) &&
    !checkingIncomplete;

  return (
    <>
      {checkingIncomplete && showModal && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1300,
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <Modal open={showSaveConfirm} onClose={handleClose}>
        <Box sx={{ ...ModalBaseStyles, minHeight: "20vh" }}>
          <ModalHeader title={title} onCloseClick={handleClose} />
          <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
            Do you wish to finish this screening?
          </Typography>
          <Typography variant="body1" fontWeight="regular">
            Continuing will make the form read only. Are you sure you want to
            continue?
          </Typography>
          <Box sx={{ ...ModalActionButtonStyles, mt: 2 }}>
            {!submitLoader ? (
              <>
                <Button onClick={handleClose} variant="outlined">
                  Cancel
                </Button>
                <Button onClick={() => void postFinish()} variant="contained">
                  Confirm
                </Button>
              </>
            ) : (
              <CircularProgress size={25} />
            )}
          </Box>
        </Box>
      </Modal>
      {showIncompleteModal && (
        <IncompleteScreeningConfirmModal
          open={showIncompleteModal}
          onClose={() => setShowIncompleteModal(false)}
          onContinue={() => setShowIncompleteModal(false)}
          incompleteForms={incompleteForms}
          loading={submitLoader}
        />
      )}
    </>
  );
};

export default FinishScreeningModal;
